import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import MainLayout from '../../components/layout/MainLayout';
import {
  SignalIcon,
  PlusIcon,
  ArrowPathIcon,
  PauseIcon,
  PlayIcon,
  TrashIcon,
  PencilIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  ChartBarIcon,
  GlobeAltIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import uptimeMonitorService, { UptimeMonitor, UptimeIncident, PaginationInfo } from '../../services/api/uptime-monitor.service';
import MonitorFormModal from './MonitorFormModal';
import MonitorDetailsModal from './MonitorDetailsModal';

export default function UptimeMonitorsPage() {
  const { t } = useTranslation('uptime');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [monitors, setMonitors] = useState<UptimeMonitor[]>([]);
  const [incidents, setIncidents] = useState<UptimeIncident[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({ total: 0, page: 1, limit: 10, totalPages: 0 });
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<'monitors' | 'incidents'>('monitors');
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingMonitor, setEditingMonitor] = useState<UptimeMonitor | null>(null);
  const [selectedMonitor, setSelectedMonitor] = useState<UptimeMonitor | null>(null);
  const [checkingId, setCheckingId] = useState<string | null>(null);

  useEffect(() => {
    loadData();
    // Refresh every 30 seconds
    const interval = setInterval(() => loadData(pagination.page), 30000);
    return () => clearInterval(interval);
  }, [statusFilter]);

  const loadData = async (page: number = 1) => {
    try {
      setLoading(true);
      const [monitorsRes, incidentsRes] = await Promise.all([
        uptimeMonitorService.getAll({ page, limit: 10, status: statusFilter !== 'all' ? statusFilter : undefined }),
        uptimeMonitorService.getIncidents(),
      ]);

      if (monitorsRes.success) {
        setMonitors(monitorsRes.data);
        if (monitorsRes.pagination) {
          setPagination(monitorsRes.pagination);
        }
      }
      if (incidentsRes.success) setIncidents(incidentsRes.data);
    } catch (error) {
      console.error('Error loading uptime data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    loadData(newPage);
  };

  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
  };

  const handleToggle = async (id: string) => {
    try {
      await uptimeMonitorService.toggle(id);
      toast.success(t('messages.monitorToggled'));
      loadData();
    } catch (error) {
      console.error('Error toggling monitor:', error);
      toast.error(t('messages.errorToggling'));
    }
  };

  const handleRunCheck = async (id: string) => {
    try {
      setCheckingId(id);
      const result = await uptimeMonitorService.runCheck(id);
      if (result.success) {
        toast.success(t('messages.checkCompleted'));
        loadData();
      }
    } catch (error) {
      console.error('Error running check:', error);
      toast.error(t('messages.errorRunningCheck'));
    } finally {
      setCheckingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    toast((toastInstance) => (
      <div className="flex flex-col space-y-3">
        <p className="font-medium text-white">{t('messages.confirmDelete')}</p>
        <div className="flex space-x-2">
          <button
            onClick={async () => {
              toast.dismiss(toastInstance.id);
              try {
                await uptimeMonitorService.delete(id);
                toast.success(t('messages.monitorDeleted'));
                loadData();
              } catch (error) {
                console.error('Error deleting monitor:', error);
                toast.error(t('messages.errorDeleting'));
              }
            }}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
          >
            {t('common:buttons.delete')}
          </button>
          <button
            onClick={() => toast.dismiss(toastInstance.id)}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition-colors text-sm font-medium"
          >
            {t('common:buttons.cancel')}
          </button>
        </div>
      </div>
    ), {
      duration: 10000,
      position: 'top-center',
    });
  };

  const handleEdit = (monitor: UptimeMonitor) => {
    setEditingMonitor(monitor);
    setShowFormModal(true);
  };

  const handleCreate = () => {
    setEditingMonitor(null);
    setShowFormModal(true);
  };

  const handleFormClose = () => {
    setShowFormModal(false);
    setEditingMonitor(null);
    loadData();
  };

  const handleViewDetails = (monitor: UptimeMonitor) => {
    setSelectedMonitor(monitor);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'up':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'down':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'degraded':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      case 'paused':
        return <PauseIcon className="h-5 w-5 text-gray-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-blue-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      up: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      down: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      degraded: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      paused: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400',
      pending: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    };
    return styles[status] || styles.pending;
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'http':
      case 'https':
        return <GlobeAltIcon className="h-4 w-4" />;
      default:
        return <SignalIcon className="h-4 w-4" />;
    }
  };

  const formatResponseTime = (ms?: number) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const formatUptime = (percentage: number) => {
    return `${percentage.toFixed(2)}%`;
  };

  const getRelativeTime = (dateString?: string) => {
    if (!dateString) return t('never');
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);

    if (diffSecs < 60) return t('timeAgo.justNow');
    if (diffMins < 60) return t('timeAgo.minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('timeAgo.hoursAgo', { count: diffHours });
    return t('timeAgo.daysAgo', { count: Math.floor(diffHours / 24) });
  };

  // Calculate stats
  const upCount = monitors.filter(m => m.status === 'up').length;
  const downCount = monitors.filter(m => m.status === 'down').length;
  const pausedCount = monitors.filter(m => m.status === 'paused').length;
  const avgUptime = monitors.length > 0
    ? monitors.reduce((acc, m) => acc + m.uptime_percentage, 0) / monitors.length
    : 100;

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl flex-shrink-0">
              <SignalIcon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
            </div>
            <div>
              <h1 className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                {t('title')}
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 hidden sm:block">
                {t('subtitle')}
              </p>
            </div>
          </div>
          <button
            onClick={handleCreate}
            className="flex items-center justify-center space-x-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl hover:from-emerald-700 hover:to-teal-700 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <PlusIcon className="h-5 w-5" />
            <span className="font-semibold text-sm sm:text-base">{t('createMonitor')}</span>
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          <div className="relative overflow-hidden rounded-xl sm:rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 p-3 sm:p-6 shadow-xl">
            <div className="absolute top-0 right-0 -mt-4 -mr-4 h-24 w-24 rounded-full bg-white/10 blur-2xl"></div>
            <div className="relative">
              <div className="flex items-center justify-between mb-2 sm:mb-4">
                <div className="p-2 sm:p-3 bg-white/20 backdrop-blur-sm rounded-lg sm:rounded-xl">
                  <CheckCircleIcon className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
                </div>
                <span className="text-xl sm:text-3xl font-bold text-white">{upCount}</span>
              </div>
              <h3 className="text-xs sm:text-sm font-medium text-white/90">{t('stats.up')}</h3>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-xl sm:rounded-2xl bg-gradient-to-br from-red-500 to-rose-600 p-3 sm:p-6 shadow-xl">
            <div className="absolute top-0 right-0 -mt-4 -mr-4 h-24 w-24 rounded-full bg-white/10 blur-2xl"></div>
            <div className="relative">
              <div className="flex items-center justify-between mb-2 sm:mb-4">
                <div className="p-2 sm:p-3 bg-white/20 backdrop-blur-sm rounded-lg sm:rounded-xl">
                  <XCircleIcon className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
                </div>
                <span className="text-xl sm:text-3xl font-bold text-white">{downCount}</span>
              </div>
              <h3 className="text-xs sm:text-sm font-medium text-white/90">{t('stats.down')}</h3>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-xl sm:rounded-2xl bg-gradient-to-br from-gray-500 to-gray-600 p-3 sm:p-6 shadow-xl">
            <div className="absolute top-0 right-0 -mt-4 -mr-4 h-24 w-24 rounded-full bg-white/10 blur-2xl"></div>
            <div className="relative">
              <div className="flex items-center justify-between mb-2 sm:mb-4">
                <div className="p-2 sm:p-3 bg-white/20 backdrop-blur-sm rounded-lg sm:rounded-xl">
                  <PauseIcon className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
                </div>
                <span className="text-xl sm:text-3xl font-bold text-white">{pausedCount}</span>
              </div>
              <h3 className="text-xs sm:text-sm font-medium text-white/90">{t('stats.paused')}</h3>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-xl sm:rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 p-3 sm:p-6 shadow-xl">
            <div className="absolute top-0 right-0 -mt-4 -mr-4 h-24 w-24 rounded-full bg-white/10 blur-2xl"></div>
            <div className="relative">
              <div className="flex items-center justify-between mb-2 sm:mb-4">
                <div className="p-2 sm:p-3 bg-white/20 backdrop-blur-sm rounded-lg sm:rounded-xl">
                  <ChartBarIcon className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
                </div>
                <span className="text-xl sm:text-3xl font-bold text-white">{formatUptime(avgUptime)}</span>
              </div>
              <h3 className="text-xs sm:text-sm font-medium text-white/90">{t('stats.avgUptime')}</h3>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-lg overflow-hidden">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <div className="flex justify-around sm:justify-start sm:space-x-8 px-2 sm:px-6">
              <button
                onClick={() => setActiveTab('monitors')}
                className={`py-3 sm:py-4 px-2 sm:px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'monitors'
                    ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
                }`}
              >
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <SignalIcon className="h-5 w-5" />
                  <span className="hidden sm:inline">{t('tabs.monitors')}</span>
                  <span className="px-1.5 sm:px-2 py-0.5 text-xs font-bold rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                    {monitors.length}
                  </span>
                </div>
              </button>

              <button
                onClick={() => setActiveTab('incidents')}
                className={`py-3 sm:py-4 px-2 sm:px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'incidents'
                    ? 'border-red-500 text-red-600 dark:text-red-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
                }`}
              >
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <ExclamationTriangleIcon className="h-5 w-5" />
                  <span className="hidden sm:inline">{t('tabs.incidents')}</span>
                  {incidents.filter(i => i.status === 'ongoing').length > 0 && (
                    <span className="px-1.5 sm:px-2 py-0.5 text-xs font-bold rounded-full bg-red-100 text-red-600 dark:bg-red-900/30">
                      {incidents.filter(i => i.status === 'ongoing').length}
                    </span>
                  )}
                </div>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6">
            {activeTab === 'monitors' && (
              <div>
                {monitors.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 mb-4">
                      <SignalIcon className="h-8 w-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      {t('noMonitors.title')}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">{t('noMonitors.description')}</p>
                    <button
                      onClick={handleCreate}
                      className="inline-flex items-center space-x-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                    >
                      <PlusIcon className="h-5 w-5" />
                      <span>{t('createMonitor')}</span>
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Filter */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-2">
                        <FunnelIcon className="h-5 w-5 text-gray-400" />
                        <select
                          value={statusFilter}
                          onChange={(e) => handleStatusFilterChange(e.target.value)}
                          className="text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded-lg focus:ring-emerald-500 focus:border-emerald-500"
                        >
                          <option value="all">{t('filter.all')}</option>
                          <option value="up">{t('status.up')}</option>
                          <option value="down">{t('status.down')}</option>
                          <option value="degraded">{t('status.degraded')}</option>
                          <option value="paused">{t('status.paused')}</option>
                          <option value="pending">{t('status.pending')}</option>
                        </select>
                      </div>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {t('pagination.showing', { from: ((pagination.page - 1) * pagination.limit) + 1, to: Math.min(pagination.page * pagination.limit, pagination.total), total: pagination.total })}
                      </span>
                    </div>

                    <div className="space-y-4">
                      {monitors.map((monitor) => (
                        <div
                          key={monitor.id}
                          className="group rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 sm:p-6 hover:shadow-lg transition-all cursor-pointer"
                          onClick={() => handleViewDetails(monitor)}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                          <div className="flex items-start space-x-4 flex-1 min-w-0">
                            <div className="flex-shrink-0">
                              {getStatusIcon(monitor.status)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2 mb-1">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                                  {monitor.name}
                                </h3>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(monitor.status)}`}>
                                  {t(`status.${monitor.status}`)}
                                </span>
                              </div>
                              <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                                {getTypeIcon(monitor.type)}
                                <span className="truncate">{monitor.url}</span>
                              </div>
                              <div className="flex flex-wrap items-center gap-4 mt-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                                <span>{t('responseTime')}: {formatResponseTime(monitor.last_response_time_ms)}</span>
                                <span>{t('uptime')}: {formatUptime(monitor.uptime_percentage)}</span>
                                <span>{t('lastCheck')}: {getRelativeTime(monitor.last_check_at)}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => handleRunCheck(monitor.id)}
                              disabled={checkingId === monitor.id}
                              className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors disabled:opacity-50"
                              title={t('actions.runCheck')}
                            >
                              <ArrowPathIcon className={`h-5 w-5 ${checkingId === monitor.id ? 'animate-spin' : ''}`} />
                            </button>
                            <button
                              onClick={() => handleToggle(monitor.id)}
                              className="p-2 text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
                              title={monitor.status === 'paused' ? t('actions.resume') : t('actions.pause')}
                            >
                              {monitor.status === 'paused' ? (
                                <PlayIcon className="h-5 w-5" />
                              ) : (
                                <PauseIcon className="h-5 w-5" />
                              )}
                            </button>
                            <button
                              onClick={() => handleEdit(monitor)}
                              className="p-2 text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-lg transition-colors"
                              title={t('actions.edit')}
                            >
                              <PencilIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleDelete(monitor.id)}
                              className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                              title={t('actions.delete')}
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    </div>

                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                      <div className="flex items-center justify-center space-x-2 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <button
                          onClick={() => handlePageChange(pagination.page - 1)}
                          disabled={pagination.page === 1}
                          className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronLeftIcon className="h-5 w-5" />
                        </button>

                        {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                          let pageNum;
                          if (pagination.totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (pagination.page <= 3) {
                            pageNum = i + 1;
                          } else if (pagination.page >= pagination.totalPages - 2) {
                            pageNum = pagination.totalPages - 4 + i;
                          } else {
                            pageNum = pagination.page - 2 + i;
                          }
                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                pagination.page === pageNum
                                  ? 'bg-emerald-600 text-white'
                                  : 'border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}

                        <button
                          onClick={() => handlePageChange(pagination.page + 1)}
                          disabled={pagination.page === pagination.totalPages}
                          className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronRightIcon className="h-5 w-5" />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {activeTab === 'incidents' && (
              <div>
                {incidents.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 mb-4">
                      <CheckCircleIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      {t('noIncidents.title')}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">{t('noIncidents.description')}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {incidents.map((incident) => (
                      <div
                        key={incident.id}
                        className={`rounded-xl border p-4 sm:p-6 ${
                          incident.status === 'ongoing'
                            ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/10'
                            : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                        }`}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                incident.status === 'ongoing'
                                  ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                                  : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                              }`}>
                                {t(`incidentStatus.${incident.status}`)}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {new Date(incident.started_at).toLocaleString()}
                              </span>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                              {incident.monitor?.name || t('unknownMonitor')}
                            </h3>
                            {incident.cause && (
                              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{incident.cause}</p>
                            )}
                            <div className="flex flex-wrap items-center gap-4 mt-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                              <span>{t('checksFailed')}: {incident.checks_failed}</span>
                              {incident.duration_seconds && (
                                <span>{t('duration')}: {Math.round(incident.duration_seconds / 60)}m</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Form Modal */}
      {showFormModal && (
        <MonitorFormModal
          monitor={editingMonitor}
          onClose={handleFormClose}
        />
      )}

      {/* Details Modal */}
      {selectedMonitor && (
        <MonitorDetailsModal
          monitor={selectedMonitor}
          onClose={() => setSelectedMonitor(null)}
        />
      )}
    </MainLayout>
  );
}
